import { useEffect, useState } from "react";
import { api, formatApiError, downloadPdf } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Download, FileText, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

function SignedCertQR({ certId }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let revoke;
    let cancelled = false;
    api
      .get(`/certificates/${certId}/qr`, { responseType: "blob" })
      .then((r) => {
        if (cancelled) return;
        const url = URL.createObjectURL(r.data);
        revoke = url;
        setSrc(url);
      })
      .catch(() => {});
    return () => { cancelled = true; if (revoke) URL.revokeObjectURL(revoke); };
  }, [certId]);
  if (!src) return <div className="h-20 w-20 rounded bg-slate-100 animate-pulse" />;
  return <img src={src} alt="QR de vérification" className="h-20 w-20 rounded border border-slate-200 bg-white" data-testid={`cert-qr-${certId}`} />;
}

function fmtStamp(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit", month: "long", year: "numeric",
      hour: "2-digit", minute: "2-digit", timeZone: "Europe/Paris",
    });
  } catch { return iso; }
}

function fmtCode(id) {
  return id ? [id.slice(0, 5), id.slice(5, 10), id.slice(10, 15)].join(" · ").toUpperCase() : "";
}

export default function EmployeeCertificates() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ type_attestation: "travail", motif: "", destinataire: "" });

  const load = async () => {
    try { const { data } = await api.get("/certificates/my"); setItems(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/certificates", form);
      toast.success("Demande envoyée à la RH");
      setOpen(false);
      setForm({ type_attestation: "travail", motif: "", destinataire: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const dl = async (id) => {
    try { await downloadPdf(`/certificates/${id}/pdf`, `attestation_${id.slice(0,8)}.pdf`); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <PageHeader
        title="Attestations de travail"
        subtitle="Demandez une attestation de travail. Une fois validée par la RH, téléchargez le PDF officiel."
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-cert-button" className="bg-[#0066FF] hover:bg-[#0052CC]"><Plus className="h-4 w-4 mr-2" />Nouvelle demande</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Nouvelle demande d'attestation</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4" data-testid="cert-form">
                <div>
                  <Label>Type d'attestation</Label>
                  <Select value={form.type_attestation} onValueChange={(v) => setForm({ ...form, type_attestation: v })}>
                    <SelectTrigger data-testid="cert-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="travail">Attestation de travail</SelectItem>
                      <SelectItem value="salaire">Attestation de salaire</SelectItem>
                    </SelectContent>
                  </Select>
                  {form.type_attestation === "salaire" && (
                    <p className="text-[11px] text-slate-500 mt-1">
                      L'attestation de salaire mentionnera votre rémunération mensuelle brute. Elle doit être renseignée par la RH pour que la demande soit acceptée.
                    </p>
                  )}
                </div>
                <div>
                  <Label>Destinataire (facultatif)</Label>
                  <Input data-testid="cert-dest-input" placeholder="Ex : Banque XYZ, Consulat…" value={form.destinataire} onChange={(e) => setForm({ ...form, destinataire: e.target.value })} />
                </div>
                <div>
                  <Label>Motif de la demande</Label>
                  <Textarea data-testid="cert-motif-input" required rows={3} placeholder="Ex : demande de crédit, démarche administrative…" value={form.motif} onChange={(e) => setForm({ ...form, motif: e.target.value })} />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
                  <Button data-testid="submit-cert-button" type="submit" disabled={loading} className="bg-[#0066FF] hover:bg-[#0052CC]">{loading ? "Envoi…" : "Soumettre"}</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <SignedHistory items={items} onDownload={dl} />

      <Card className="border-slate-200 overflow-hidden">
        {items.length === 0 ? (
          <div className="p-12 text-center">
            <FileText className="h-10 w-10 text-slate-300 mx-auto" />
            <p className="mt-3 text-sm text-slate-500">Aucune demande d'attestation</p>
          </div>
        ) : (
          <>
          <div className="hidden md:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Destinataire</TableHead>
                <TableHead>Motif</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((c) => (
                <TableRow key={c.id} data-testid={`cert-row-${c.id}`}>
                  <TableCell className="text-sm text-slate-600">{new Date(c.created_at).toLocaleDateString("fr-FR")}</TableCell>
                  <TableCell className="text-sm">{c.type_attestation === "salaire" ? "Salaire" : "Travail"}</TableCell>
                  <TableCell>{c.destinataire || "—"}</TableCell>
                  <TableCell className="max-w-xs truncate">{c.motif}</TableCell>
                  <TableCell><StatusBadge status={c.status} /></TableCell>
                  <TableCell className="text-right">
                    {c.status === "approuve" ? (
                      <Button data-testid={`download-cert-${c.id}`} size="sm" variant="outline" onClick={() => dl(c.id)}><Download className="h-3.5 w-3.5 mr-1" />Télécharger</Button>
                    ) : <span className="text-xs text-slate-400">—</span>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
          <div className="md:hidden divide-y divide-slate-100">
            {items.map((c) => (
              <div key={c.id} className="p-4" data-testid={`cert-card-${c.id}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0">
                    <div className="font-medium text-slate-900">Attestation de {c.type_attestation === "salaire" ? "salaire" : "travail"}</div>
                    <div className="text-xs text-slate-500 truncate mt-0.5">Pour : {c.destinataire || "à qui de droit"}</div>
                    <div className="text-[11px] text-slate-400 mt-0.5">{new Date(c.created_at).toLocaleDateString("fr-FR")}</div>
                  </div>
                  <StatusBadge status={c.status} />
                </div>
                {c.motif && <p className="text-xs text-slate-600 line-clamp-2 mb-3">{c.motif}</p>}
                {c.status === "approuve" && (
                  <Button data-testid={`download-cert-m-${c.id}`} size="sm" variant="outline" className="w-full" onClick={() => dl(c.id)}>
                    <Download className="h-3.5 w-3.5 mr-1" />Télécharger le PDF
                  </Button>
                )}
              </div>
            ))}
          </div>
          </>
        )}
      </Card>
    </div>
  );
}

function SignedHistory({ items, onDownload }) {
  const signed = (items || [])
    .filter((c) => c.status === "approuve" && c.signature_id)
    .sort((a, b) => (b.signed_at || "").localeCompare(a.signed_at || ""));
  if (signed.length === 0) return null;
  return (
    <section className="mb-6" data-testid="signed-history">
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck className="h-4 w-4 text-emerald-600" />
        <h2 className="font-heading font-semibold text-slate-900 text-lg">
          Historique des attestations signées
        </h2>
        <span className="text-xs text-slate-500">({signed.length})</span>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {signed.map((c) => {
          const isSalaire = c.type_attestation === "salaire";
          return (
            <Card key={c.id} className="border-slate-200 p-4 flex gap-4" data-testid={`signed-card-${c.id}`}>
              <SignedCertQR certId={c.id} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full px-2 py-0.5">
                    <ShieldCheck className="h-3 w-3" /> Signée
                  </span>
                  <span className="text-xs text-slate-500">
                    {isSalaire ? "Salaire" : "Travail"}
                  </span>
                </div>
                <div className="font-medium text-slate-900 mt-1 truncate">
                  Pour : {c.destinataire || "à qui de droit"}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Signée le <span className="text-slate-700">{fmtStamp(c.signed_at)}</span>
                </div>
                <div className="text-[11px] font-mono text-slate-500 mt-0.5 truncate" title={c.signature_id}>
                  {fmtCode(c.signature_id)}
                </div>
                <Button
                  data-testid={`signed-download-${c.id}`}
                  size="sm" variant="outline" className="mt-3"
                  onClick={() => onDownload(c.id)}
                >
                  <Download className="h-3.5 w-3.5 mr-1" /> Télécharger le PDF
                </Button>
              </div>
            </Card>
          );
        })}
      </div>
    </section>
  );
}
